import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import TabMenu from "../tabmenu/TabMenu";
import "./claimcheck.css";
import { getThemeAssetUrl, toCssUrlValue, useImageWithFallback, FALLBACK_TICKET_IMAGE } from "../../../functions/themeAssets";

const parseObject = (val) => {
  if (val == null) return {};
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return typeof p === "object" && p !== null && !Array.isArray(p) ? p : {};
    } catch {
      return {};
    }
  }
  return {};
};

const parseArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const formatClaimLabel = (key) =>
  key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");

const formatTimestamp = (value) => {
  if (value == null || value === "") return "";

  try {
    if (typeof value === "object") {
      const seconds = value.seconds ?? value._seconds;
      const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
      if (seconds != null) {
        const ms = Number(seconds) * 1000 + Number(nanoseconds) / 1e6;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
      }
      if (typeof value.toDate === "function") {
        const d = value.toDate();
        if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
      }
    }

    if (typeof value === "number") {
      const ms = value > 1e12 ? value : value * 1000; // seconds vs milliseconds
      const d = new Date(ms);
      if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
    }

    if (typeof value === "string") {
      const asNum = Number(value);
      if (!Number.isNaN(asNum)) {
        const ms = asNum > 1e12 ? asNum : asNum * 1000;
        const d = new Date(ms);
        if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
      }

      const d = new Date(value);
      if (!isNaN(d.getTime())) return d.toLocaleString("en-IN");
    }
  } catch {
    // ignore and fallback to raw string
  }

  return String(value);
};

/** Truthy only when value is non-null, non-empty, and not "null" or "null null" (case-insensitive). */
const hasValidName = (val) => {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  if (s === "") return false;
  if (s === "null" || s === "null null") return false;
  return true;
};

/** Which cell indices to highlight for a claim type (0–11). early_five / unlucky_five use released match; handled separately. */
const getHighlightIndices = (claimType) => {
  switch (claimType) {
    case "top_line":
      return [0, 1, 2, 3];
    case "middle_line":
      return [4, 5, 6, 7];
    case "bottom_line":
      return [8, 9, 10, 11];
    case "four_corners":
      return [0, 3, 8, 11];
    case "full_house":
    case "full_house_two":
    case "full_house_three":
      return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    default:
      return [];
  }
};

/** For early_five / unlucky_five: indices in ticket (0–11) where ticket number is in released list. */
const getEarlyOrUnluckyHighlightIndices = (ticketNumbers, releasedNumbers, caseInsensitive = true) => {
  const indices = [];
  const released = (releasedNumbers || []).map((r) =>
    caseInsensitive ? String(r).toLowerCase() : String(r)
  );
  (ticketNumbers || []).forEach((cell, i) => {
    const cellStr = caseInsensitive ? String(cell).toLowerCase() : String(cell);
    if (released.some((r) => r === cellStr)) indices.push(i);
  });
  return indices;
};

const ClaimCheck = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data } = useSelector((state) => state.theme);
  const ticketBgUrl = useImageWithFallback(
    data?.ticket ? getThemeAssetUrl(data.ticket) : "",
    FALLBACK_TICKET_IMAGE
  );

  const levelFromUrl = searchParams.get("levelname") || "top_line";
  const [currentClaim, setCurrentClaim] = useState(levelFromUrl);
  const [claimsList, setClaimsList] = useState([]);
  const [claimCounts, setClaimCounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    setCurrentClaim(levelFromUrl);
  }, [levelFromUrl]);

  const claimsConfig = useMemo(() => {
    if (!data || !data.claims) return {};
    return parseObject(data.claims);
  }, [data]);

  const enabledClaimKeys = useMemo(
    () => Object.keys(claimsConfig).filter((k) => claimsConfig[k]),
    [claimsConfig]
  );

  const tableColor = useMemo(() => {
    if (!data) return ["#f0f0f0", "#e0e0e0"];
    if (typeof data.table_color1 !== "undefined" && typeof data.table_color2 !== "undefined")
      return [data.table_color1, data.table_color2];
    const arr = parseArray(data.table_color);
    if (Array.isArray(arr) && arr.length >= 2) return [arr[0], arr[1]];
    return ["#f0f0f0", "#e0e0e0"];
  }, [data]);

  const textColor = data?.text_color || "#333";
  const valuesArray = useMemo(() => parseArray(data?.values_array || []), [data]);
  const shuffle = useMemo(() => parseArray(data?.shuffle || []), [data]);
  

  useEffect(() => {
    if (!adminToken || !organizationId || !sessionId) {
      setClaimCounts(null);
      return;
    }
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/checkClaims`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({ organizationId, sessionId }),
    })
      .then((res) => res.json().catch(() => ({})))
      .then((json) => {
        if (json.success && json.counts && typeof json.counts === "object") {
          setClaimCounts(json.counts);
        } else {
          setClaimCounts({});
        }
      })
      .catch(() => setClaimCounts({}));
  }, [adminToken, organizationId, sessionId]);

  useEffect(() => {
    if (!adminToken || !organizationId || !sessionId || !currentClaim) {
      setClaimsList([]);
      return;
    }
    setLoading(true);
    const fetchClaims = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const url = `${base}/claims`;
        const res = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ claim: currentClaim, organizationId, sessionId }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(json.list)) {
          setClaimsList(json.list);
        } else {
          setClaimsList([]);
        }
      } catch {
        setClaimsList([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClaims();
  }, [adminToken, organizationId, sessionId, currentClaim]);

  const selectClaim = (key) => {
    setSearchParams({ levelname: key });
    setCurrentClaim(key);
  };

  const openTicket = (row) => {
    setModalRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRow(null);
  };

  const getReleasedAtClaim = (whenReleased) => {
    const when = Math.max(0, parseInt(whenReleased, 10) || 0);
    const cells = [];
    for (let i = 0; i < when && i < shuffle.length; i++) {
      const shuffleIdx = shuffle[i];
      const idx =
        typeof shuffleIdx === "string" ? parseInt(shuffleIdx, 10) - 1 : (shuffleIdx - 1);
      const value = valuesArray[idx];
      cells.push(value != null ? String(value) : "");
    }
    return cells;
  };

  const highlightIndicesForModal = useMemo(() => {
    if (!modalRow) return [];
    const claim = modalRow.claim || currentClaim;
    const numbers = modalRow.numbers || [];
    const released = modalRow.releasedNumbers || getReleasedAtClaim(modalRow.when_released);

    if (claim === "early_five" || claim === "unlucky_five") {
      return getEarlyOrUnluckyHighlightIndices(
        numbers,
        released,
        claim === "early_five"
      );
    }
    return getHighlightIndices(claim);
  }, [modalRow, currentClaim, shuffle, valuesArray]);

  if (!data) {
    return (
      <div className="claimcheck-page">
        <div className="claimcheck-loading">Loading theme...</div>
      </div>
    );
  }

  return (
    <div className="claimcheck-page">
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/rules")}
        >
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <TabMenu />

      <div className="claimcheck-card-wrap">
        <div className="claimcheck-card">
          <div className="claimcheck-card-header">View all claims</div>
          <div className="claimcheck-card-body">
            <div className="claimcheck-buttons">
              {enabledClaimKeys.map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`claimcheck-btn ${currentClaim === key ? "active" : ""}`}
                  onClick={() => selectClaim(key)}
                >
                  {formatClaimLabel(key)} ({claimCounts && claimCounts[key] !== undefined ? claimCounts[key] : "0"})
                </button>
              ))}
            </div>

            <div className="claimcheck-table-wrap">
              {loading ? (
                <div className="claimcheck-loading">Loading claims...</div>
              ) : (
                <table className="claimcheck-table">
                  <thead>
                    <tr>
                      {claimsList.some((row) => hasValidName(row.name)) && <th>Name</th>}
                      <th>Email</th>
                      <th>Rank</th>
                      <th>Category</th>
                      <th>Tikit</th>
                      <th>Rel Pos</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claimsList.length === 0 ? (
                      <tr>
                        <td colSpan={claimsList.some((row) => hasValidName(row.name)) ? 7 : 6} className="claimcheck-empty">
                          No claims for this category.
                        </td>
                      </tr>
                    ) : (
                      claimsList.map((row, idx) => (
                        <tr key={row.id || idx}>
                          {claimsList.some((r) => hasValidName(r.name)) && (
                            <td>{hasValidName(row.name) ? row.name : (row.emp_code || "—")}</td>
                          )}
                          <td>{row.email || row.emp_code || "—"}</td>
                          <td>{idx + 1}</td>
                          <td>{formatClaimLabel(row.claim || currentClaim)}</td>
                          <td>
                            <button
                              type="button"
                              className="claimcheck-open-btn"
                              onClick={() =>
                                openTicket({
                                  ...row,
                                  numbers: row.numbers || [],
                                  releasedNumbers: getReleasedAtClaim(row.when_released),
                                })
                              }
                            >
                              Open
                            </button>
                          </td>
                          <td>{row.when_released ?? "—"}</td>
                          <td>{formatTimestamp(row.timestamp) || "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {modalOpen && modalRow && (
        <div className="claimcheck-modal-overlay" role="dialog" aria-modal="true">
          <div className="claimcheck-modal">
            <div className="claimcheck-modal-body">
              <div 
                className="fullhouse-final-ticket"
                 style={{
                    backgroundImage: toCssUrlValue(ticketBgUrl),
                    backgroundSize: "100% 100%",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    padding: data.ticket ? "26px 80px" : "26px 80px",
                  }}
                >
                
                <table className="fullhouse-final-table">
                  <tbody style={{border:"1px solid #000"}}>
                    <tr>
                      {[0, 1, 2, 3].map((i) => {
                        const numbers = modalRow.numbers || [];
                        const value = numbers[i] != null ? String(numbers[i]) : "";
                        const highlight = highlightIndicesForModal.includes(i);
                        return (
                          <React.Fragment key={i}>
                            <td
                              className={`fullhouse-final-cell ${highlight ? "highlight" : ""}`}
                              style={{
                                backgroundColor: highlight ? "#ffeb3b" : tableColor[0],
                                color: textColor,
                              }}
                            >
                              {value || "\u00a0"}
                            </td>
                            <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor[1] }} />
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor[1] }} />
                      {[4, 5, 6, 7].map((i, idx) => {
                        const numbers = modalRow.numbers || [];
                        const value = numbers[i] != null ? String(numbers[i]) : "";
                        const highlight = highlightIndicesForModal.includes(i);
                        return (
                          <React.Fragment key={i}>
                            <td
                              className={`fullhouse-final-cell ${highlight ? "highlight" : ""}`}
                              style={{
                                backgroundColor: highlight ? "#ffeb3b" : tableColor[0],
                                color: textColor,
                              }}
                            >
                              {value || "\u00a0"}
                            </td>
                            {idx < 3 && <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor[1] }} />}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                    <tr>
                      {[8, 9, 10, 11].map((i) => {
                        const numbers = modalRow.numbers || [];
                        const value = numbers[i] != null ? String(numbers[i]) : "";
                        const highlight = highlightIndicesForModal.includes(i);
                        return (
                          <React.Fragment key={i}>
                            <td
                              className={`fullhouse-final-cell ${highlight ? "highlight" : ""}`}
                              style={{
                                backgroundColor: highlight ? "#ffeb3b" : tableColor[0],
                                color: textColor,
                              }}
                            >
                              {value || "\u00a0"}
                            </td>
                            <td className="fullhouse-final-spacer" style={{ backgroundColor: tableColor[1] }} />
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="claimcheck-modal-footer">
              <button type="button" className="claimcheck-close-btn" onClick={closeModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClaimCheck;
